import { Fragment, useState } from "react";
import { CircularProgress } from "@mui/material";
import { EyeSlashIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import ShowToast from "../../components/alerts/all_toasts";
import { persistAuthV2Session } from "../../helpers/authSession";
import { RouteConstants } from "../../helpers/RouteConstants";
import Button from "../../components/Button";
import BaseService from "../../helpers/baseServices";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import OtpVerifyStep from "../../components/auth/OtpVerifyStep";

type Account = { id: string; first_name: string; last_name: string };

const SignInScreen = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isPhoneError, setIsPhoneError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Set when login comes back PASSWORD_SETUP_REQUIRED — the account has
  // no password yet (first login after registration, or a migrated
  // account) and an OTP has already been sent for it.
  const [needsSetup, setNeedsSetup] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);

  const handleLogin = async () => {
    const formatPhoneNumber = phoneNumber?.replace(/\s+/g, "");
    if (!phoneNumber) {
      setIsPhoneError(true);
      return;
    }
    setIsPhoneError(false);
    setIsBusy(true);
    try {
      const response = await BaseService.post_api(
        RouteConstants.SIGN_IN_ROUTE,
        { identifier: formatPhoneNumber, password: password || undefined },
      );
      const payload = response?.data?.payload;
      if (payload?.status === "OK") {
        ShowToast.success_toast("Login successful");
        persistAuthV2Session(payload);
        navigate("/home", { replace: true });
      } else if (payload?.status === "PASSWORD_SETUP_REQUIRED") {
        setAccounts(payload?.accounts ?? []);
        setNeedsSetup(true);
      }
    } catch (error: any) {
      ShowToast.error_toast(error?.response?.data?.message);
    } finally {
      setIsBusy(false);
    }
  };

  if (needsSetup) {
    const formatPhoneNumber = phoneNumber?.replace(/\s+/g, "");
    return (
      <div className="h-screen bg-neutral-50 overflow-hidden flex flex-col">
        <div className="w-full px-[30px] mt-[30%]">
          {accounts.length > 1 && (
            <p className="body-text-2 pb-3">
              This number is shared by {accounts.length} accounts — the code
              will sign in to{" "}
              {accounts.map((a) => `${a.first_name} ${a.last_name}`).join(", ")}.
            </p>
          )}
          <OtpVerifyStep
            identifier={formatPhoneNumber}
            purpose="LOGIN"
            title="Set your password"
            subtitle={`Enter the code sent to ${formatPhoneNumber} and choose a password.`}
            submitLabel="Sign in"
            onBack={() => setNeedsSetup(false)}
            onSuccess={(payload) => {
              ShowToast.success_toast("Login successful");
              persistAuthV2Session(payload);
              navigate("/home", { replace: true });
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="h-screen bg-neutral-50 overflow-hidden flex flex-col">
        <div className="w-full px-[30px] mt-[40%]">
          <div className="header-text-3">Sign In</div>
          <div className="body-text-2 pt-1">Welcome back!</div>
          <div>
            <div className="flex flex-col mt-8 relative">
              <small
                className={`${isPhoneError ? "text-red-500" : ""} mb-2 ml-1`}
              >
                Your mobile number
              </small>
              <PhoneInput
                inputClassName="input"
                defaultCountry="gh"
                hideDropdown
                placeholder="Enter your phone number"
                inputStyle={{
                  width: "100%",
                  height: "50px",
                  fontSize: "18px",
                  border: "1px solid #939291",
                  padding: "8px",
                  borderTopRightRadius: "8px",
                  borderBottomRightRadius: "8px",
                }}
                value={phoneNumber}
                onChange={(phone: any) => setPhoneNumber(phone)}
                disabled={isBusy}
              />
            </div>
            <div className="flex flex-col mt-4 relative">
              <small className="mb-2 ml-1">Your password</small>
              <input
                className="input"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="absolute right-2 bottom-[10px]">
                {showPassword ? (
                  <button onClick={() => setShowPassword(!showPassword)}>
                    <EyeIcon className="w-6 h-6 text-black" />
                  </button>
                ) : (
                  <button onClick={() => setShowPassword(!showPassword)}>
                    <EyeSlashIcon className="w-6 h-6 text-black" />
                  </button>
                )}
              </div>
            </div>
            <p className="text-neutral-300 mt-1 ml-1">
              First time, or forgot your password? Leave this blank and we'll
              text you a code.
            </p>
            <div className="flex items-center justify-between mx-1 mt-1">
              <button
                className="btn-subtle"
                onClick={() => navigate("/forgot-password")}
              >
                <h3 className="text-[#F15B50] font-light">
                  Forgot password?
                </h3>
              </button>
            </div>

            <div className="mt-[30px] flex justify-center items-center">
              <Button
                width="w-full"
                onClick={() => handleLogin()}
                disabled={isBusy}
              >
                {isBusy ? (
                  <div className="w-full flex justify-center items-center">
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  </div>
                ) : (
                  <span>Sign In</span>
                )}
              </Button>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <p className="text-neutral-300">Don't have an account?</p>
            <Link className="btn-subtle " to="/signup">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default SignInScreen;
